<template>
  <div class="static-page">
    <div v-for="dynamicComponent in pageContent">
      <div
        class="pickup-point-viewer-container"
        v-if="dynamicComponent.__typename === 'ComponentStaticPageMap'"
      >
        <pickup-points-viewer
          :mode="pickupPointsViewerMode"
          :auto-select-first="pickupPointsAutoSelectFirst"
          :full-screen-mobile="false"
          :pickup-points="pickupPoints.map(pp=>pp)"
        />
      </div>
      <div
        v-if="dynamicComponent.__typename === 'ComponentBlogBlogContent'"
        v-html="dynamicComponent.Content"
        @click="handleContentClick"
      />

      <VueShowdown
        v-if="dynamicComponent.__typename === 'ComponentStaticPageMarkdown'"
        :markdown="dynamicComponent.Markdown"
        :options="{ tables: true }"
      />

      <bike-table
        v-if="dynamicComponent.__typename === 'ComponentStaticPageBikeSizeTable'"
      />
    </div>
  </div>
</template>

<script>
import { VueShowdown } from 'vue-showdown';
import BikeTable from './blocks/Static/BikeTable.vue';
import PickupPointsViewer from '../core/blocks/PickupPoint/PickupPointsViewer';
import { currentStoreView } from '@vue-storefront/core/lib/multistore';

export default {
  name: 'StaticContent',
  components: {
    VueShowdown,
    PickupPointsViewer,
    BikeTable
  },
  metaInfo () {
    return {
      title: this.title
    }
  },
  props: {
    id: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    pageContent: {
      type: Array,
      required: true
    }
  },
  computed: {
    pickupPoints () {
      return this.$store.getters['partner/getPickupPoints'];
    },
    storeView () {
      return currentStoreView();
    },
    pickupPointsViewerMode () {
      return this.storeView.pickupPoints?.viewerMode || 'switch';
    },
    pickupPointsAutoSelectFirst () {
      return this.storeView.pickupPoints?.autoSelectFirst !== false;
    }
  },
  data () {
    return {}
  },
  methods: {
    handleContentClick (event) {
      // Handle anchor tags for internal navigation
      if (event.target.tagName === 'A') {
        const href = event.target.getAttribute('href');
        
        if (href) {
          // Check if it's an external link
          if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
            // External link - let it work normally
            return;
          }
          
          // Check if it's an anchor link (same page)
          if (href.startsWith('#')) {
            // Let browser handle anchor scrolling
            return;
          }
          
          // Internal link - use Vue Router to prevent page reload
          if (href.startsWith('/')) {
            event.preventDefault();
            this.$router.push(href);
            return;
          }
          
          // Relative internal link
          event.preventDefault();
          this.$router.push('/' + href);
        }
      }
      
      // Handle special CTA buttons with data-route
      if (event.target.classList.contains('article-cta-button')) {
        event.preventDefault();

        const route = event.target.getAttribute('data-route');
        if (route) {
          this.$router.push(route);
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/colors';

$color-partner: color(partner);
$color-darken-partner-10: color(darken-partner-10);

.article-cta-button {
  margin: 30px auto 20px auto;
  background-color: $color-partner;
  color: white;
  border-radius: 25px;
  padding: 10px 20px;
  transition: background-color 0.3s ease;
  display: block;
  text-align: center;
  text-decoration: none;
  width: fit-content;
  cursor: pointer;
  &:hover {
    background-color: $color-darken-partner-10;
    color: white;
    text-decoration: none;
  }
}
.pickup-point-viewer-container {
    width: 100%;
    height: 500px;
    padding-bottom: 50px;
}

.static-page {
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 20px auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    // Override any inline styles from CKEditor
    width: auto !important;

    // Responsive behavior
    @media (max-width: 768px) {
      margin: 15px auto;
      border-radius: 4px;
    }

    @media (max-width: 480px) {
      margin: 10px auto;
      max-width: calc(100% - 20px);
    }
  }

  // Handle figure elements if CKEditor wraps images in them
  figure {
    margin: 20px auto;

    img {
      margin: 0;
    }

    figcaption {
      margin-top: 8px;
      font-size: 14px;
      color: #666;
      font-style: italic;
    }
  }

  table {
    width: 100%;

    th {
      text-align: left;
    }

    tr {
      td:nth-of-type(1) {
        width: 200px;
      }

      td:nth-of-type(2) {
        color: rgb(70,70,70);
      }
    }
  }
}

</style>
